// Pure helpers for the Home dashboard. Kept in their own file so the tricky
// timezone / merge / relative-time logic stays unit-testable (utils.test.js).

// Today's date key ("YYYY-MM-DD") in Israel time, to match the server's
// RecordingPlanRow.date (Asia/Jerusalem). en-CA formats as ISO yyyy-mm-dd.
export const todayKeyIsrael = (now = new Date()) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jerusalem" }).format(now);

// HH:mm in Israel time for a UTC epoch-ms value.
export const formatTimeIsrael = (utcMs) =>
  new Intl.DateTimeFormat("he-IL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Jerusalem",
  }).format(new Date(utcMs));

// Compact relative age. Returns { unit } for "now", else { n, unit } where unit
// is a label key the caller translates ("m" | "h" | "d").
export const timeAgo = (ts, now = Date.now()) => {
  const s = Math.max(0, Math.floor((now - ts) / 1000));
  if (s < 60) return { unit: "now" };
  const m = Math.floor(s / 60);
  if (m < 60) return { n: m, unit: "m" };
  const h = Math.floor(m / 60);
  if (h < 24) return { n: h, unit: "h" };
  return { n: Math.floor(h / 24), unit: "d" };
};

// Editor route for a content item (reframer opens in the reframe editor).
export const articlePath = (item) =>
  `${item?.source === "reframer" ? "/reframe-editor" : "/editor"}/${item?.id}`;

// Deep link into TrendScout's All tab, which the page then rings and scrolls to.
export const trendPath = (trend) =>
  trend?.guid
    ? `/trendscout?guid=${encodeURIComponent(trend.guid)}`
    : "/trendscout";

// Latest passed trends for display (drop the status sentinel row + rejects).
export const passedTrends = (items = [], limit = 6) =>
  items.filter((t) => t.kind !== "status" && t.verdict === "pass").slice(0, limit);

// Flatten today's recording plan into per-show rows, earliest first.
// Each: { title, startUtcMs, status }.
export const todayRecordings = (plan = [], now = new Date()) => {
  const key = todayKeyIsrael(now);
  return plan
    .filter((r) => r.date === key)
    .flatMap((r) =>
      (r.segments || []).map((s) => ({
        title: s.title,
        startUtcMs: s.showStartUtcMs,
        status: r.status,
      }))
    )
    .filter((s) => s.title)
    .sort((a, b) => a.startUtcMs - b.startUtcMs);
};

// Merge the four feeds into one recency-sorted activity timeline.
// Each entry: { kind, title, ts, to }  (to = deep-link route).
export const buildActivity = (
  { articles = [], highlights = [], trends = [], plan = [] },
  limit = 8
) => {
  const items = [];
  for (const a of articles)
    items.push({
      kind: "article",
      title: a.title,
      ts: +new Date(a.created),
      to: articlePath(a),
    });
  for (const h of highlights)
    items.push({
      kind: "highlight",
      title: h.generated_title || h.title,
      ts: +new Date(h.created_at),
      to: "/highlights",
    });
  for (const t of trends)
    items.push({
      kind: "trend",
      title: t.hebrewTitle || t.originalTitle,
      ts: +new Date(t.analyzedAt),
      to: trendPath(t),
    });
  for (const row of plan)
    if (row.status === "captured")
      for (const seg of row.segments || [])
        items.push({
          kind: "recording",
          title: seg.title,
          ts: seg.showEndUtcMs,
          to: "/highlights",
        });
  return items
    .filter((x) => x.title && Number.isFinite(x.ts))
    .sort((a, b) => b.ts - a.ts)
    .slice(0, limit);
};
